import axios from 'axios'
import { toast } from 'react-toastify'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'

// Create axios instance with improved connection settings
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // HTTP connection settings for better reliability
  httpAgent: false, // Let browser handle HTTP agent
  httpsAgent: false, // Let browser handle HTTPS agent
  // Keep connections alive (browser handles this automatically)
  maxRedirects: 5,
  validateStatus: (status) => status >= 200 && status < 300, // Throw on 4xx/5xx so error interceptor runs (401→refresh, 403→toast, etc.)
})

// Error message deduplication - prevent showing same error multiple times
const errorMessageCache = new Map()
const ERROR_DEDUP_WINDOW = 5000 // 5 seconds
const NETWORK_ERROR_DEDUP_WINDOW = 30000 // 30 seconds for network errors

// Global error suppression state - suppress all errors after too many consecutive failures
let consecutiveNetworkErrors = 0
let errorSuppressionActive = false
let lastSuppressionMessage = null
let lastSuppressionTime = 0
const MAX_CONSECUTIVE_ERRORS = 10 // Increased from 3 to 10 to allow more errors before suppression
const SUPPRESSION_RESET_WINDOW = 30000 // Reduced to 30 seconds for faster recovery

// Retry configuration
const MAX_RETRIES = 3
const RETRY_DELAY = 1000 // Start with 1 second
const RETRY_DELAY_MULTIPLIER = 2 // Exponential backoff

// Connection health tracking
let lastSuccessfulRequest = Date.now()
let isBackendHealthy = true
const HEALTH_CHECK_INTERVAL = 30000 // Check backend health every 30 seconds

// Detailed error logging
const logErrorDetails = (error, context = '') => {
  const timestamp = new Date().toISOString()
  const errorDetails = {
    timestamp,
    context,
    message: error.message,
    code: error.code,
    response: error.response ? {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data
    } : null,
    request: error.config ? {
      method: error.config.method,
      url: error.config.url,
      baseURL: error.config.baseURL
    } : null,
    stack: error.stack
  }
  
  // Log to console with details
  console.group(`🔴 API Error ${context ? `(${context})` : ''}`)
  console.error('Timestamp:', timestamp)
  console.error('Error Message:', error.message)
  console.error('Error Code:', error.code)
  if (error.response) {
    console.error('Response Status:', error.response.status)
    console.error('Response Data:', error.response.data)
  }
  if (error.config) {
    console.error('Request Method:', error.config.method?.toUpperCase())
    console.error('Request URL:', error.config.url)
  }
  console.error('Full Error Details:', errorDetails)
  console.groupEnd()
  
  // Store in localStorage for debugging (last 10 errors)
  try {
    const errorLog = JSON.parse(localStorage.getItem('apiErrorLog') || '[]')
    errorLog.push(errorDetails)
    // Keep only last 10 errors
    if (errorLog.length > 10) {
      errorLog.shift()
    }
    localStorage.setItem('apiErrorLog', JSON.stringify(errorLog))
  } catch (e) {
    // Ignore localStorage errors
  }
}

const showErrorToast = (message, isNetworkError = false) => {
  const now = Date.now()
  
  // If suppression is active, check if enough time has passed to reset
  if (errorSuppressionActive) {
    // If enough time has passed since last suppression, reset and show this error
    if ((now - lastSuppressionTime) > SUPPRESSION_RESET_WINDOW) {
      resetErrorTracking()
      // Fall through to show the error normally
    } else {
      // Still in suppression window - suppress this error
      return
    }
  }
  
  // Check deduplication cache
  const dedupWindow = isNetworkError ? NETWORK_ERROR_DEDUP_WINDOW : ERROR_DEDUP_WINDOW
  const lastShown = errorMessageCache.get(message)
  
  // Only show if not shown in the deduplication window
  if (!lastShown || (now - lastShown) > dedupWindow) {
    errorMessageCache.set(message, now)
    toast.error(message)
    
    // Clean up old entries periodically
    if (errorMessageCache.size > 100) {
      const cutoff = now - Math.max(ERROR_DEDUP_WINDOW, NETWORK_ERROR_DEDUP_WINDOW)
      for (const [msg, timestamp] of errorMessageCache.entries()) {
        if (timestamp < cutoff) {
          errorMessageCache.delete(msg)
        }
      }
    }
  }
}

// Track consecutive errors and activate suppression if needed
const trackNetworkError = () => {
  consecutiveNetworkErrors++
  
  // Activate suppression after too many consecutive errors
  if (consecutiveNetworkErrors >= MAX_CONSECUTIVE_ERRORS && !errorSuppressionActive) {
    errorSuppressionActive = true
    lastSuppressionTime = Date.now()
    console.warn('Too many consecutive network errors. Suppressing error toasts to prevent spam.')
  }
}

// Reset error tracking on successful API call
const resetErrorTracking = () => {
  if (consecutiveNetworkErrors > 0 || errorSuppressionActive) {
    const wasSuppressed = errorSuppressionActive
    consecutiveNetworkErrors = 0
    errorSuppressionActive = false
    lastSuppressionMessage = null
    lastSuppressionTime = 0
    lastSuccessfulRequest = Date.now()
    isBackendHealthy = true
    
    if (wasSuppressed) {
      console.log('✅ Error suppression reset - Backend connection restored')
    }
  }
}

// Retry logic with exponential backoff
const retryRequest = async (config, retryCount = 0) => {
  if (retryCount >= MAX_RETRIES) {
    return Promise.reject(new Error('Max retries exceeded'))
  }
  
  const delay = RETRY_DELAY * Math.pow(RETRY_DELAY_MULTIPLIER, retryCount)
  await new Promise(resolve => setTimeout(resolve, delay))
  
  return api(config)
}

// Check backend health
const checkBackendHealth = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL.replace(/\/api\/?$/, '')}/health`,
      { timeout: 5000 }
    )
    if (response.data && response.data.success) {
      isBackendHealthy = true
      lastSuccessfulRequest = Date.now()
      return true
    }
  } catch (error) {
    isBackendHealthy = false
    console.warn('Backend health check failed:', error.message)
  }
  return false
}

// Periodic health check
setInterval(() => {
  const timeSinceLastSuccess = Date.now() - lastSuccessfulRequest
  if (timeSinceLastSuccess > HEALTH_CHECK_INTERVAL && !isBackendHealthy) {
    checkBackendHealth()
  }
}, HEALTH_CHECK_INTERVAL)

// Request interceptor to add auth token and company context
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage (AuthContext stores it there)
    const token = localStorage.getItem('token')
    
    // Add authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add company context header (get from user object in localStorage)
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.companyId) {
          config.headers['X-Company-Id'] = user.companyId
        }
      } catch (error) {
        console.warn('Error parsing user data:', error)
      }
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    // Reset error tracking on successful response
    resetErrorTracking()
    
    // Log response time for debugging (only slow requests or in dev mode)
    if (response.config.metadata) {
      const endTime = new Date()
      const duration = endTime - response.config.metadata.startTime
      // Only log slow requests (> 2 seconds) or in development
      if (duration > 2000 || import.meta.env.DEV) {
        console.log(`✅ API Call: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`)
      }
    }
    
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Skip retry if already retried or if it's a non-retryable error
    const shouldRetry = !originalRequest._retry && 
                       !originalRequest._skipRetry &&
                       (error.code === 'ERR_NETWORK' || 
                        error.code === 'ECONNABORTED' || 
                        error.response?.status === 429 ||
                        error.response?.status === 500 ||
                        error.response?.status === 503 ||
                        error.response?.status === 504)
    
    // Retry network errors automatically
    if (shouldRetry) {
      originalRequest._retry = originalRequest._retry || 0
      originalRequest._retry++
      
      if (originalRequest._retry <= MAX_RETRIES) {
        const retryDelay = RETRY_DELAY * Math.pow(RETRY_DELAY_MULTIPLIER, originalRequest._retry - 1)
        console.warn(`🔄 Retrying request (${originalRequest._retry}/${MAX_RETRIES}) after ${retryDelay}ms: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`)
        return retryRequest(originalRequest, originalRequest._retry - 1)
      } else {
        console.error(`❌ Max retries (${MAX_RETRIES}) exceeded for: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`)
        logErrorDetails(error, 'Max Retries Exceeded')
      }
    }
    
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retryAuth) {
      originalRequest._retryAuth = true
      
      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            { refreshToken },
            { timeout: 10000 }
          )
          
          const { token, refreshToken: newRefreshToken } = response.data
          
          // Update localStorage with new tokens
          localStorage.setItem('token', token)
          localStorage.setItem('refreshToken', newRefreshToken)
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, clear auth data and redirect to login
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }
    
    // Handle other errors
    if (error.response) {
      const { status, data } = error.response
      
      // Mark that we've handled this error so components don't show duplicate messages
      error._errorHandled = true
      
      // Check if it's a database/server error (500) - treat as network/database error
      const isDatabaseError = status === 500 || status === 503 || status === 504
      if (isDatabaseError) {
        trackNetworkError()
        isBackendHealthy = false
      }
      
      // Log error details for debugging
      if (status >= 500 || status === 429) {
        logErrorDetails(error, `HTTP ${status}`)
      }
      
      switch (status) {
        case 400:
          showErrorToast(data.message || 'Bad Request', false)
          break
        case 403:
          showErrorToast('Access Denied', false)
          break
        case 404:
          showErrorToast('Resource Not Found', false)
          break
        case 422:
          showErrorToast(data.message || 'Validation Error', false)
          break
        case 429:
          trackNetworkError()
          console.warn('⚠️ Rate limit exceeded - Too many requests')
          showErrorToast('Too many requests - Please wait a moment and try again', true)
          break
        case 500:
        case 503:
        case 504:
          trackNetworkError()
          isBackendHealthy = false
          console.error(`❌ Server error (${status}) - Backend may be down or overloaded`)
          showErrorToast('Server error - The backend may be temporarily unavailable. Please try again in a moment.', true)
          break
        default:
          showErrorToast(data.message || 'An error occurred', isDatabaseError)
      }
    } else if (error.request) {
      // Network error - mark as handled and track for suppression
      error._errorHandled = true
      trackNetworkError()
      isBackendHealthy = false
      
      // Log detailed error information
      logErrorDetails(error, 'Network Error')
      
      // Check if it's a timeout or connection error
      if (error.code === 'ECONNABORTED') {
        console.warn('⏱️ Request timeout - Server may be slow or overloaded')
        showErrorToast('Request timeout - The server may be slow or unavailable. Please try again.', true)
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        console.error('🌐 Network connection error - Backend may be down or unreachable')
        showErrorToast('Network Error - Please check your connection and ensure the backend server is running', true)
      } else {
        console.error('🔌 Connection error:', error.code)
        showErrorToast('Network Error - Please check your connection', true)
      }
    } else {
      // Other error - mark as handled
      error._errorHandled = true
      logErrorDetails(error, 'Unexpected Error')
      showErrorToast('An unexpected error occurred', false)
    }
    
    return Promise.reject(error)
  }
)

// API service methods
export const apiService = {
  // Generic CRUD operations
  get: (url, config = {}) => api.get(url, config),
  post: (url, data, config = {}) => api.post(url, data, config),
  put: (url, data, config = {}) => api.put(url, data, config),
  patch: (url, data, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
  
  // File upload
  upload: (url, formData, config = {}) => 
    api.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
    }),
  
  // File download
  download: (url, config = {}) => 
    api.get(url, {
      ...config,
      responseType: 'blob',
    }),
  
  // Batch operations
  batch: (requests) => 
    Promise.all(requests.map(request => api(request))),
}

// Specific API endpoints
export const authAPI = {
  login: (credentials) => apiService.post('/auth/login', credentials),
  logout: () => apiService.post('/auth/logout'),
  refresh: (refreshToken) => apiService.post('/auth/refresh', { refreshToken }),
  forgotPassword: (email) => apiService.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => apiService.post('/auth/reset-password', { token, password }),
  changePassword: (data) => apiService.post('/auth/change-password', data),
  profile: () => apiService.get('/auth/profile'),
  updateProfile: (data) => apiService.put('/auth/profile', data),
}

export const companyAPI = {
  list: () => apiService.get('/companies'),
  get: (id) => apiService.get(`/companies/${id}`),
  create: (data) => apiService.post('/companies', data),
  update: (id, data) => apiService.put(`/companies/${id}`, data),
  delete: (id) => apiService.delete(`/companies/${id}`),
  switch: (id) => apiService.post(`/companies/${id}/switch`),
  settings: (id) => apiService.get(`/companies/${id}/settings`),
  updateSettings: (id, data) => apiService.put(`/companies/${id}/settings`, data),
}

export const userAPI = {
  list: (params) => apiService.get('/users', { params }),
  get: (id) => apiService.get(`/users/${id}`),
  create: (data) => apiService.post('/users', data),
  update: (id, data) => apiService.put(`/users/${id}`, data),
  delete: (id) => apiService.delete(`/users/${id}`),
  roles: () => apiService.get('/users/roles'),
  permissions: () => apiService.get('/users/permissions'),
}

export const customerAPI = {
  list: (params) => apiService.get('/customers', { params }),
  get: (id) => apiService.get(`/customers/${id}`),
  create: (data) => apiService.post('/customers', data),
  update: (id, data) => apiService.put(`/customers/${id}`, data),
  delete: (id) => apiService.delete(`/customers/${id}`),
  search: (query) => apiService.get(`/customers/search?q=${encodeURIComponent(query)}`),
}

export const vendorAPI = {
  list: (params) => apiService.get('/vendors', { params }),
  get: (id) => apiService.get(`/vendors/${id}`),
  create: (data) => apiService.post('/vendors', data),
  update: (id, data) => apiService.put(`/vendors/${id}`, data),
  delete: (id) => apiService.delete(`/vendors/${id}`),
  search: (query) => apiService.get(`/vendors/search?q=${encodeURIComponent(query)}`),
}

export const projectAPI = {
  list: (params) => apiService.get('/projects', { params }),
  get: (id) => apiService.get(`/projects/${id}`),
  create: (data) => apiService.post('/projects', data),
  update: (id, data) => apiService.put(`/projects/${id}`, data),
  delete: (id) => apiService.delete(`/projects/${id}`),
  tasks: (id) => apiService.get(`/projects/${id}/tasks`),
  timesheets: (id, params) => apiService.get(`/projects/${id}/timesheets`, { params }),
  expenses: (id, params) => apiService.get(`/projects/${id}/expenses`, { params }),
}

export const timesheetAPI = {
  list: (params) => apiService.get('/timesheets', { params }),
  get: (id) => apiService.get(`/timesheets/${id}`),
  create: (data) => apiService.post('/timesheets', data),
  update: (id, data) => apiService.put(`/timesheets/${id}`, data),
  delete: (id) => apiService.delete(`/timesheets/${id}`),
  approve: (id) => apiService.post(`/timesheets/${id}/approve`),
  reject: (id, reason) => apiService.post(`/timesheets/${id}/reject`, { reason }),
}

export const invoiceAPI = {
  list: (params) => apiService.get('/invoices', { params }),
  get: (id) => apiService.get(`/invoices/${id}`),
  create: (data) => apiService.post('/invoices', data),
  update: (id, data) => apiService.put(`/invoices/${id}`, data),
  delete: (id) => apiService.delete(`/invoices/${id}`),
  send: (id) => apiService.post(`/invoices/${id}/send`),
  markPaid: (id, data) => apiService.post(`/invoices/${id}/mark-paid`, data),
  download: (id) => apiService.download(`/invoices/${id}/download`),
}

export const reportAPI = {
  dashboard: () => apiService.get('/reports/dashboard'),
  sales: (params) => apiService.get('/reports/sales', { params }),
  projects: (params) => apiService.get('/reports/projects', { params }),
  timesheets: (params) => apiService.get('/reports/timesheets', { params }),
  expenses: (params) => apiService.get('/reports/expenses', { params }),
  export: (type, params) => apiService.get(`/reports/export/${type}`, { params }),
}

export default api

